ApiAjax = require 'app/scripts/network/api-ajax'
ApiError = require 'app/scripts/network/api-error'
Backbone = require 'app/scripts/lib/backbone'
Hearsay = require 'hearsay'
Promise = require 'bluebird'
Queue = require 'promise-queue'
SchedulingMixin = require 'app/scripts/lib/scheduling-mixin'
Util = require 'app/scripts/lib/util'
_ = require 'underscore'
{ mixin } = require 'app/scripts/lib/mixin_DO_NOT_USE'
{ track } = require '@trello/analytics'
{ classicUpdaterClient } = require 'app/scripts/network/classic-updater-client'
{ client, syncDeltaToCache } = require '@trello/graphql'

module.exports = mixin SchedulingMixin, class TrelloModel extends Backbone.Model
  constructor: (attributes, options) ->
    @_lazyCache ?= {}
    @_marked = {}
    @_lockQueue = {}
    @modelCache = options?.modelCache
    # We only want to run one API call on a model, at a time
    @_apiQueue = new Queue(1)

    super
    @modelCache?.add(@, { source: options?.source })

  _initSubEventsOn: (attr) ->
    @listenTo @, "change:#{attr}", (model, newAttrs) =>
      oldAttrs = model.previousAttributes()?[attr]
      for key, value of newAttrs when (!oldAttrs? || value != oldAttrs[key])
        @trigger "change:#{ attr }.#{ key }"

  waitForLock: (name, next) ->
    fxReleaseLock = =>
      if _.isEmpty(@_lockQueue[name])
        delete @_lockQueue[name]
      else
        fx = @_lockQueue[name].shift()
        fx(fxReleaseLock)

    if @_lockQueue[name]?
      # Someone else has the lock, we have to wait
      @_lockQueue[name].push(next)
    else
      # Nobody has the lock, let's take it
      @_lockQueue[name] = []
      next(fxReleaseLock)

  set: (attrs, options) ->
    # If we got back an array response, update ourselved from the correct entry
    # in the array
    if _.isArray(attrs)
      attrs = _.find(attrs, (a) -> a.id == @id || a._id == @id)

    if attrs?._id?
      attrs.id ?= attrs._id
      delete attrs._id

    slashValues = ([key, value] for key, value of attrs when key.indexOf('/') >= 0)
    if slashValues.length > 0
      attrs = _.clone(attrs)
      # Translate "foo/bar":"baz" -> foo.bar:baz
      for [key, value] in slashValues
        [parent, children..., leaf] = key.split('/')

        # We have to be careful to *clone* the existing value, otherwise
        # we'll modify the actual value, and Backbone won't see the change
        attrs[parent] ?= _.clone(@get(parent)) ? {}
        cur = attrs[parent]
        for child, i in children
          cur = cur[child] = _.clone(cur[child]) ? {}
        cur[leaf] = value
        delete attrs[key]

    if options?.broadcast && @id
      classicUpdaterClient.broadcast({
        typeName: @typeName,
        delta: Object.assign({ @id }, attrs)
      })

    super

  get: (attr) ->
    if @attributes
      super

  @lazy: (map) ->
    properties = Object.entries(map).reduce (properties, [key, value]) ->
      properties[key] =
        get: ->
          if !@_lazyCache[key]
            collection = map[key].call(@)
            @destruct(collection)
            @_lazyCache[key] = collection
          @_lazyCache[key]
        set: (value) ->
          throw new Error("Can't set a lazy list")
      properties
    , {}

    Object.defineProperties @::, properties

  destructor: ->
    @stopListening()
    @cancelScheduled()

    if @_using?
      for obj in @_using
        obj.destructor?()

    Util.shred(@)

  destroy: ->
    # We have to call the Backbone destroy first, so it'll properly
    # delegate the remove/destroy events
    super
    @destructor()

  destruct: (objs) ->
    if !_.isArray(objs)
      @destruct([objs])
    else
      @_using ?= []
      for obj in objs
        @_using.push(obj)

  @load: (id, payload, modelCache, isCachedModelValidPredicates = []) ->
    # Dependency required at call site to avoid import cycles, do not lift to top of module
    ModelLoader = require 'app/scripts/db/model-loader'

    Promise.try =>
      if !modelCache? || !id?
        return null

      onlyHas = (obj, allowedKeys) ->
        keys = _.keys(obj)
        !_.any keys, (key) -> key !in allowedKeys

      # Only use the cache if we can guarantee it's as good as using loadModel
      # … which is only eashy to check if we're just asking for fields
      # … OR if the caller passed a set of predicates to check if the model is valid
      if (!onlyHas(payload, ['query']) || !onlyHas(payload.query, ['fields'])) &&
          _.isEmpty(isCachedModelValidPredicates)
        return null

      if (existingModel = modelCache.get(@, id))?
        fields = if payload.query.fields
          payload.query.fields.split(',')
        else
          []

        if _.all(fields, (field) -> existingModel.attributes.hasOwnProperty(field)) &&
            _.all(isCachedModelValidPredicates, (predicate) -> predicate(existingModel))
          return existingModel

      null

    .then (existingModel) =>
      if existingModel?
        return existingModel

      ModelLoader.loadModel(@, id, payload)

  ready: (callback) ->
    return if (!callback)

    if (@isReady)
      callback()
    else
      if (!@waitingForReady)
        @waitingForReady = []

      @waitingForReady.push(callback)

    return

  setReady: ->
    @isReady = true

    if (@waitingForReady)
      waitingForReady = @waitingForReady
      @waitingForReady = null
      callback?() for callback in waitingForReady

    return

  # update the model with specified params.
  # You can call this with a single key, value, or with several key/values in a {}
  # (key, value[, opts][, next])
  # (params[, opts][, next])
  update: (args...) ->
    if _.isString(args[0])
      [key, value, [opts]..., next] = args
      params = {}
      params[key] = value
    else
      [params, [opts]..., next] = args

    if !opts? && !_.isFunction(next)
      opts = next
      next = null

    { waitForServer } = opts ? {}
    if not waitForServer
      # We want the change to take place on the client immediately; this is the default
      # because it makes the UI more responsive
      @set(params)

      # Sync optimstic updates to the ApolloCache
      if (@typeName && @id)
        delta = Object.assign({ @id }, params)
        syncDeltaToCache(client, @typeName, delta)

    { debounceSaveInterval } = opts ? 0
    if debounceSaveInterval
      # sometimes we want to reflect changes in the client immediately, but hold off on sending the call to the server. Things whose interface can change quickly like checkboxes, labels, and reactions can benefit from this.

      @persistUpdateDebounced ?=
        _.debounce @_persistUpdate, debounceSaveInterval

      @persistUpdateDebounced(params, next)
    else
      @_persistUpdate(params, next)

  _persistUpdate: (params, next) ->
    @api {
      type: 'put'
      data: params
    }, next

  addToSet: (attr, value, [opts]..., next) ->
    if !opts? && !_.isFunction(next)
      opts = next
      next = ->
    { waitForServer, collectionName } = opts ? {}
    model = @
    collectionName ?= attr

    if not waitForServer
      newValue = _.uniq(model.get(attr) ? []).concat(value)
      @set(attr, newValue)

      # Sync optimistic addToSets to the Apollo Cache
      if (@typeName && @id)
        delta = {}
        delta[attr] = newValue
        delta.id = @id
        syncDeltaToCache(client, @typeName, delta)

    @api {
      method: collectionName
      data: { value }
      success: (resp, next) =>
        data = {}
        data[attr] = _.uniq(model.get(attr).concat(value))
        @modelCache.enqueueDelta(model, data)
        next? null, resp
    }, next

  pull: (attr, value, [opts]..., next) ->
    if !opts? && !_.isFunction(next)
      opts = next
      next = ->
    { waitForServer, collectionName } = opts ? {}
    model = @
    collectionName ?= attr

    if not waitForServer
      newValue = _.without(model.get(attr) ? [], value)
      @set(attr, newValue)

      # Sync optimistic pulls to ApolloCache
      if (@typeName && @id)
        delta = {}
        delta[attr] = newValue
        delta.id = @id
        syncDeltaToCache(client, @typeName, delta)

    @api {
      method: "#{ collectionName }/#{ value }"
      type: 'delete'
      success: (resp, next) =>
        data = {}
        data[attr] = _.without(model.get(attr), value)
        @modelCache.enqueueDelta(model, data)
        next? null, resp
    }, next

  run: (method, data, next) ->
    @api {
      method
      data
    }, next

  api: ({ method, type, data, success, datatype }, next) ->
    model = @
    datatype ?= ''
    method ?= ''
    type ?= 'post'
    data ?= {}
    success ?= (resp, next) =>
      if resp.id == model.id
        @modelCache.enqueueDelta(model, resp)
      else if _.isArray(resp)
        # Maybe we got back an array of items, and one of the items is the model
        # that we just modified
        entry = _.find(resp, (e) -> e.id == model.id)
        if entry?
          @modelCache.enqueueDelta(model, entry)

      next null, resp
      return
    next ?= ->

    @waitForId @, =>
      url = @url()
      if method? && method != ""
        url += "/#{ method }"

      Promise.using @modelCache.getLock(), =>
        @_apiQueue.add ->
          new Promise (resolve, reject) ->
            ApiAjax {
              url
              type
              data
              datatype
              success: (data) ->
                resolve Promise.fromNode (next) ->
                  success(data, next)
                return
              error: ({ status, responseText }, textStatus, error, fxDefault) ->
                error = ApiError.fromResponse(status, responseText)
                # See card https://trello.com/c/W3jvOvi8 for a discussion of the
                # history of how we got to dropping 4xx errors and where we think
                # that may be causing apparent data loss
                if error instanceof ApiError.Server || error instanceof ApiError.NoResponse
                  track("TrelloModel", "Show Server Error", responseText)
                  fxDefault()
                else
                  track("TrelloModel", "Hidden Server Error", responseText)
                reject error
                return
            }
      .nodeify(next).catch(->).done()
    @

  toggle: (attr, value, add, [opts]..., next) ->
    @[if add then 'addToSet' else 'pull'] attr, value, opts, next

  snoop: (attr) ->
    if attr?
      new Hearsay.ContinuousSignal (send) =>
        send(@get('attr'))
        update = =>
          send(@get('attr'))
        @listenTo @, "change:#{ attr }", update
        =>
          @stopListening @, "change:#{ attr }", update
    else
      new Hearsay.ContinuousSignal (send) =>
        send(@)
        update = =>
          send(@)
        @listenTo @, 'change', update
        =>
          @stopListening @, 'change', update

  isVisibleAction: (action) -> true

  getActions: (modelsForFiltering...) ->
    @modelCache.all('Action').filter (action) =>
      action.includesModel(@) &&
      _.all(modelsForFiltering, (model) -> action.includesModel(model)) &&
      @isVisibleAction(action)
